import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2 } from '@withjoy/joykit';

import { UserNote } from './UserNote';
import { HotelCTA } from './HotelCTA';
import { useTranslation } from '@shared/core';
import { CostBreakdown } from './CostBreakdown';
import { StarRating, UserRating } from '@shared/components/Ratings';
import { TravelLogistics } from './TravelLogistics';
import { OtaProviderName } from './OtaProviderName';
import { withWindow } from '@shared/utils/withWindow';
import { Skeleton } from '@shared/components/Skeleton';
import { getValidCoordinates } from '@shared/core/places';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { SOLD_OUT_CTA_ALLOWED_SOURCES, HotelSearchSource } from '@shared/constants/accommodations';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { useFeaturedPlacementTelemetry } from '@apps/stays/AccommodationsConsole/FeaturedPlacementTelemetryContext';
import { SponsoredTag } from './SponsoredTag';
import { PrioritizedTagPills } from './PrioritizedTagPills';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { getSelectedRedirectTelemetryName, getSelectedRedirectTravelOfferProvider } from '@shared/utils/extractHotelRedirects';
import { affiliateHandoffTelemetry } from '@shared/utils/formatting/affiliateHandoff';
import { otaTransferredPricingTelemetry } from '../otaTransferredPricing.telemetry';
import type { AccommodationListingDTO, AccommodationsConsoleState, StandardHotelPricingValues, HotelRoomBlockPricingValues } from '@shared/types/Accommodations.types';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';

type ListingEntryMobileV2Props = Readonly<{
  noOfNights: number;
  listing: AccommodationListingDTO;
  state: AccommodationsConsoleState;
  sourceName: HotelSearchSource;
  showRoomBlockNoteAndDate: boolean;
  renderListingCta?: (listing: AccommodationListingDTO, redirectUrl?: string) => React.ReactNode;
  /** Extra photo badge. Replaces the listing's own by default; `stackBadges` shows both. */
  imageBadge?: React.ReactNode;
  /** Stack `imageBadge` under the listing's own. Opt-in — the default keeps the live guest tile. */
  stackBadges?: boolean;
  /** Optional cost-area override (e.g. the cash-fund split). Falls back to CostBreakdown. */
  renderCostBreakdown?: (pricingInfo: StandardHotelPricingValues | HotelRoomBlockPricingValues) => React.ReactNode;
  /** Optional CTA background override (e.g. the cash-fund gold). */
  ctaBackgroundColor?: string;
  /** Hover fill for `ctaBackgroundColor`; without it the button does not react to hover. */
  ctaHoverBackgroundColor?: string;
  /**
   * When set, a tap on the card calls this and opens no link, also when the link is disabled. A tap on the
   * CTA still runs the CTA's own action: the default "See availability" CTA opens the link.
   */
  onSelect?: (listing: AccommodationListingDTO) => void;
  /** When false, the "Couple's Pick" photo badge is not rendered. Defaults to true. */
  shouldShowCouplesPick?: boolean;
  /**
   * When set, replaces the price area, also while the price loads and when the listing has no price.
   * Takes precedence over `renderCostBreakdown`. The CTA below the price area is not affected.
   *
   * It may hold controls: a tap on a link or a button inside it belongs to that control, not to the card.
   */
  renderPriceArea?: (listing: AccommodationListingDTO) => React.ReactNode;
  /** When set, renders under the guest rating. */
  renderListingTag?: (listing: AccommodationListingDTO) => React.ReactNode;
}>;

/** The photo column's width and the card's vertical padding, exported so an overlay rendered beside the card can line up with the photo. */
export const LISTING_ENTRY_MOBILE_PHOTO_WIDTH = 140;
export const LISTING_ENTRY_MOBILE_PADDING_Y = 4;

const ListingEntryMobileV2 = ({
  listing,
  state,
  noOfNights,
  sourceName,
  showRoomBlockNoteAndDate,
  renderListingCta,
  imageBadge,
  stackBadges,
  renderCostBreakdown,
  ctaBackgroundColor,
  ctaHoverBackgroundColor,
  onSelect,
  shouldShowCouplesPick = true,
  renderPriceArea,
  renderListingTag,
}: ListingEntryMobileV2Props) => {
  const parseUrl = useSafeUrlParser();
  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);
  const telemetry = useListingsPanelTelemetry();
  // Read, never resolved here — see FeaturedPlacementTelemetryContext.
  const featuredPlacementTelemetry = useFeaturedPlacementTelemetry();
  const { tq } = useTranslation('accommodationsConsole');

  const isSoldOut = listing.type === 'standardHotel' && !!listing.isSoldOutForSelectedDates;
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  // Treatment lets a sold-out tile open the PDP. Scoped to the guest/admin search view so only those tiles are bucketed.
  const isSoldOutPdpScopedSource = sourceName === HotelSearchSource.GUEST_MAP || sourceName === HotelSearchSource.ADMIN_MAP;
  const [isSoldOutPdpEnabled] = useIsFeatureEnabled('accommodationsSoldOutPdpExperiment', { skip: !isSoldOut || !isSoldOutPdpScopedSource });
  const isLinkEnabled = !isSoldOut || isSoldOutPdpEnabled;

  // Treatment routes a sold-out tile to the hotel PDP by skipping the interceptor on the redirect page.
  const redirectUrl = getRedirectUrl(listing, { skipAvailabilityCheck: isSoldOut && isSoldOutPdpEnabled });

  const handleNavigation = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({
      joyPlaceId: listing.joyPlaceId || '',
      hotelName: listing.name,
      isSponsored: listing.isSponsored ?? false,
      isDirectBooking: listing.isDirectBooking ?? false,
      chain: listing.chain,
      isCoupleFavourite: listing.isFavorite ?? false,
      priceSource: listing.pricingInfo.source,
      travelOfferProvider: getSelectedRedirectTravelOfferProvider(listing.redirectMechanisms),
      ...otaTransferredPricingTelemetry(listing),
      ...featuredPlacementTelemetry,
    });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'ListingEntryMobileV2.handleNavigation');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          joyPlaceId: listing.joyPlaceId || '',
          destinationName: getSelectedRedirectTelemetryName(listing.redirectMechanisms),
          isSponsored: listing.isSponsored ?? false,
          isDirectBooking: listing.isDirectBooking ?? false,
          chain: listing.chain,
          isCoupleFavourite: listing.isFavorite ?? false,
          ...affiliateHandoffTelemetry(redirectUrl),
          ...otaTransferredPricingTelemetry(listing),
          ...mergedUtm,
          ...featuredPlacementTelemetry,
        });
      }
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  const isCardClickable = !!onSelect || isLinkEnabled;
  const activateCard = () => {
    if (onSelect) {
      onSelect(listing);
    } else {
      handleNavigation();
    }
  };

  // The card is a `role="button"` element, not a `<button>`, so it can hold real controls (a CTA, a Select button) the way
  // the desktop card does. A click on one of them, or on a photo arrow, belongs to that control and not to the card.
  const handleCardClick = useEventCallback((e: React.MouseEvent<HTMLElement>) => {
    const control = (e.target as HTMLElement).closest('a, button, [role="button"], [data-carousel-control]');
    if (control && control !== e.currentTarget) {
      return;
    }
    activateCard();
  });

  // Enter and Space activate the card as they did the native button, only when the card itself has focus.
  const handleCardKeyDown = useEventCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
      e.preventDefault();
      activateCard();
    }
  });

  const pointOfInterest = useMemo(() => {
    const location = getValidCoordinates(state.circularRegionSearch);
    if (!location) return null;

    let displayName = state.searchLocation || tq`venue`;
    if (state.searchLocation === state.venue.name) displayName = tq`venue`;

    return { displayName, location };
  }, [state.circularRegionSearch, state.searchLocation, state.venue.name, tq]);

  const shouldShowHotelCTAButton = SOLD_OUT_CTA_ALLOWED_SOURCES.includes(sourceName);
  const listingBadgeText = listing.type === 'roomBlock' ? tq`🛌 Room Block` : shouldShowCouplesPick && listing.isPickedByCouple ? tq`🏩 Couple's Pick` : null;

  const images = useMemo(
    () => (listing.mediaAssets?.photos?.length ? listing.mediaAssets.photos.map(p => ({ src: p.url, alt: listing.name })) : [{ src: DEFAULT_HOTEL_IMAGE, alt: listing.name }]),
    [listing.mediaAssets?.photos, listing.name],
  );

  return (
    <Flex
      gap={2}
      role={isCardClickable ? 'button' : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      // Kept from the native <button> this used to be: its content inherited centred text, and lines that set no
      // alignment of their own (a wrapped price note) would otherwise move to the left.
      textAlign="center"
      paddingY={LISTING_ENTRY_MOBILE_PADDING_Y}
      flexDirection="row"
      cursor={isCardClickable ? 'pointer' : 'default'}
      onClick={isCardClickable ? handleCardClick : undefined}
      onKeyDown={isCardClickable ? handleCardKeyDown : undefined}
    >
      <HotelPhotoCarousel
        hotel={listing}
        images={images}
        navigation={{
          variant: 'minimal',
          visible: 'default',
        }}
        context="Accommodations.ListingEntryMobileV2"
        borderRadius={12}
        width={LISTING_ENTRY_MOBILE_PHOTO_WIDTH}
        flexShrink={0}
        alignSelf="stretch"
      >
        {/* With `stackBadges` both show; without it the added badge replaces the listing's own. */}
        {(listingBadgeText || imageBadge) && (
          <Flex
            position="absolute"
            top={pxToRem(9)}
            left={pxToRem(6)}
            flexDirection="column"
            alignItems="flex-start"
            rowGap={pxToRem(6)}
          >
            {listingBadgeText && (!imageBadge || stackBadges) && (
              <TextV2
                color="mono14"
                paddingX={pxToRem(8)}
                paddingY={pxToRem(4)}
                borderRadius={pxToRem(12)}
                typographyVariant="label1"
                backgroundColor="rgba(255, 255, 255, 0.72)"
              >
                {listingBadgeText}
              </TextV2>
            )}
            {imageBadge}
          </Flex>
        )}
        {listing.isSponsored && <SponsoredTag />}
      </HotelPhotoCarousel>

      <Flex
        gap={2}
        flex={1}
        paddingLeft={3}
        flexDirection="column"
        justifyContent="space-between"
      >
        <Flex
          flexDirection="column"
          gap={3}
        >
          <PrioritizedTagPills tags={listing.tags} />
          <Flex
            flexDirection="column"
            gap={2}
          >
            <TextV2
              color="mono14"
              textAlign="start"
              typographyVariant="hed1"
            >
              {listing.name}
            </TextV2>

            {listing.starRating && <StarRating rating={listing.starRating} />}
          </Flex>

          {listing.userRating && (
            <UserRating
              rating={listing.userRating.ratingValue}
              reviewCount={listing.userRating.ratingCount}
            />
          )}

          {renderListingTag?.(listing)}

          {pointOfInterest && (
            <TravelLogistics
              listing={listing}
              useShortWording={true}
              pointOfInterest={pointOfInterest}
            />
          )}

          <OtaProviderName otaProvider={listing.otaProvider} />

          {listing.type === 'roomBlock' && listing.userNote && showRoomBlockNoteAndDate && <UserNote note={listing.userNote} />}
        </Flex>

        <Flex flexDirection="column">
          {/* A custom price area holds its own button, which sits flush with the photo's bottom edge. */}
          <Box
            paddingTop={5}
            paddingBottom={renderPriceArea ? 0 : 5}
          >
            {renderPriceArea ? (
              renderPriceArea(listing)
            ) : listing.pricingInfo.isLoading ? (
              <CostBreakdown.Skeleton />
            ) : listing.pricingInfo.values ? (
              renderCostBreakdown ? (
                renderCostBreakdown(listing.pricingInfo.values)
              ) : (
                <CostBreakdown
                  noOfNights={noOfNights}
                  occupancies={state.occupancies}
                  pricingInfo={listing.pricingInfo.values}
                />
              )
            ) : null}
          </Box>

          {isSoldOut && (
            <Box marginBottom={5}>
              <SoldOutTag eventDate={createDateFromUnformattedString(state.searchDates.checkin)} />
            </Box>
          )}

          {shouldShowHotelCTAButton &&
            (renderListingCta ? (
              renderListingCta(listing, redirectUrl)
            ) : (
              <HotelCTA
                listing={listing}
                redirectUrl={redirectUrl}
                sourceName={sourceName}
                backgroundColor={ctaBackgroundColor}
                hoverBackgroundColor={ctaHoverBackgroundColor}
              />
            ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

ListingEntryMobileV2.Skeleton = () => (
  <Flex
    gap={2}
    paddingY={LISTING_ENTRY_MOBILE_PADDING_Y}
    flexDirection="row"
  >
    <Skeleton
      width={LISTING_ENTRY_MOBILE_PHOTO_WIDTH}
      flexShrink={0}
      borderRadius={12}
      alignSelf="stretch"
    />

    <Flex
      gap={2}
      flex={1}
      paddingLeft={3}
      flexDirection="column"
      justifyContent="space-between"
    >
      <Flex
        flexDirection="column"
        gap={3}
      >
        <Flex
          gap={2}
          flexDirection="column"
        >
          <Skeleton
            height={20}
            width="80%"
          />

          <Flex
            alignContent="center"
            justifyContent="space-between"
          >
            <Skeleton
              height={16}
              width={80}
            />
            <Skeleton
              height={16}
              width={60}
            />
          </Flex>
        </Flex>

        <Skeleton
          height={16}
          width="50%"
        />

        <Skeleton
          height={14}
          width="60%"
        />
      </Flex>

      <Flex
        gap={2}
        flexDirection="column"
      >
        <CostBreakdown.Skeleton />
      </Flex>
    </Flex>
  </Flex>
);

export { ListingEntryMobileV2 };
